import React, { useEffect, useState } from "react";
import { Monitor, Smartphone, Sun, Moon, Save, Eye, ChevronRight, User, Palette, Image as ImageIcon, Table2, MousePointer, SlidersHorizontal } from "lucide-react";
import ColorField from "../components/ColorField";
import ColorGroupSection from "../components/ColorGroupSection";
import ImageUploadField from "../components/ImageUploadField";
import LoginPreview from "../components/LoginPreview";
import GeneralPreview from "../components/GeneralPreview";
import { COLOR_SECTIONS } from "../components/colorDefinitions";
import { useDispatch } from "react-redux";
import { licenseService } from "../../../Services/license.service";
import { storage } from "../../../Services/storage.service";
import { storageKeys } from "../../../Constants/storage.key";
import { useTheme as useNewTheme } from "../../../contexts/NewThemeContext";
import { useTranslation } from "react-i18next";

const DEFAULT_COLORS = {
  light_theme: {
    themePrimary: "#1F3A5F", themeSecondary: "#162B45", themeTertiary: "#FF7A1A", themeSupport: "#FFD1A8",
    bgPrimary: "#FFFFFF", bgSecondary: "#F4F7FB", bgTertiary: "#E6EDF5", bgCard: "#FFFFFF",
    bgSidebar: "#F4F7FB", bgNavbar: "#EDF2F9", bgContent: "#F8FAFD", bgGrid: "#E0E6EF",
    bgGridAltrow: "#FFFFFF", textPrimary: "#1F3A5F", textSecondary: "#5A6D85", textMuted: "#A5B1C2",
    textInverse: "#FFFFFF", borderPrimary: "#D6DEE8", borderSecondary: "#EDF2F9", borderFocus: "#FF7A1A",
    statusSuccess: "#28A745", statusWarning: "#FF7A1A", statusDanger: "#DC3545", statusInfo: "#1F3A5F",
    bgScrollBar: "#FF7A1A",
    menu: { bgMain: "#F4F7FB", bgEnabled: "#E6EDF5", textDisabled: "#A5B1C2", textEnabled: "#1F3A5F", textHover: "#FF7A1A" },
    cards: { background: "#FFFFFF", subtitle: "#5A6D85", value: "#FF7A1A" },
    filters: { background: "#FFFFFF", title: "#1F3A5F", subtitle: "#162B45", border: "#D6DEE8" },
    tables: { headerBg: "#1F3A5F", text: "#1F3A5F", rowBg: "#FFFFFF", rowBgAlt: "#F4F7FB", paginationText: "#FF7A1A", paginationHover: "#EDF2F9" },
    buttons: { bgBtnPrimary: "#1F3A5F", bgBtnSecondary: "#5A6D85", bgBtnTertiary: "#FF7A1A", btnSuccess: "#28A745", textPrimary: "#FFFFFF", textSecondary: "#FFFFFF", textHoverPrimary: "#FFFFFF", textHoverSecondary: "#FFFFFF" },
  },
  dark_theme: {
    bgPrimary: "#0E1C30", bgSecondary: "#0A1424", bgTertiary: "#152841", bgCard: "#0F2137",
    bgSidebar: "#0A1424", bgNavbar: "#0E1C30", bgContent: "#0E1C30", bgGrid: "#1C3554",
    bgGridAltrow: "#152841", textPrimary: "#FFFFFF", textSecondary: "#B0BDD0", textMuted: "#6C7C95",
    textInverse: "#1F3A5F", borderPrimary: "#1C3554", borderSecondary: "#152841", borderFocus: "#FF7A1A",
    bgScrollBar: "#FF7A1A", themePrimary: "#1F3A5F", themeTertiary: "#FF7A1A",
    menu: { bgMain: "#0A1424", bgEnabled: "#09111F", textDisabled: "#B0BDD0", textEnabled: "#FFFFFF", textHover: "#FF7A1A" },
    cards: { background: "#0F2137", title: "#FFFFFF", subtitle: "#B0BDD0", value: "#FF7A1A" },
    filters: { background: "#0A1424", title: "#FF7A1A", subtitle: "#FFFFFF", border: "#1C3554" },
    tables: { headerBg: "#1F3A5F", text: "#FFFFFF", rowBg: "#0E1C30", rowBgAlt: "#152841", paginationText: "#FF7A1A", paginationHover: "#FFFFFF" },
    buttons: { bgBtnPrimary: "#1F3A5F", bgBtnSecondary: "#5A6D85", bgBtnTertiary: "#FF7A1A", btnSuccess: "#28A745", textPrimary: "#FFFFFF", textSecondary: "#FFFFFF", textHoverPrimary: "#FFFFFF", textHoverSecondary: "#FFFFFF" },
  }
};

const IMAGES_DEFAULT = { companyLogo: "", condensedMenu: "", favicon: "", homeScreenImage: "", internalLogo: "" };

const TABS = [
  { id: "licensee", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.LICENSEE", icon: User },
  { id: "images", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.IMAGES", icon: ImageIcon },
  { id: "geral", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.GENERAL_COLORS", icon: Palette },
  { id: "menu", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.MENU", icon: SlidersHorizontal },
  { id: "cards", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.CARDS", icon: Monitor },
  { id: "filters", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.FILTERS", icon: SlidersHorizontal },
  { id: "tables", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.TABLES", icon: Table2 },
  { id: "buttons", labelKey: "PREVIEWS.CREATE_LICENSED.TABS.BUTTONS", icon: MousePointer },
];

const FIXED_DOMAIN = process.env.REACT_APP_FIXED_DOMAIN || "https://v3.olharfixo.com.br/";

export default function CreateLicensed() {
  const { t } = useTranslation();
  const currentTheme = useNewTheme();
  const [activeTab, setActiveTab] = useState("licensee");
  const [themeMode, setThemeMode] = useState("light_theme");
  const [previewMode, setPreviewMode] = useState("general");
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [images, setImages] = useState(IMAGES_DEFAULT);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const settings = storage.get(storageKeys.settings);
  const clientLicensedId = settings?.clientLicensedId ?? "";
  const existingCompanyLogo = currentTheme?.companyLogo ?? "";
  const existingHomeScreenImage = currentTheme?.companyMainImageUrl ?? "";
  const existingFavicon = currentTheme?.favicon ?? "";
  const existingCondensedMenu = currentTheme?.condensedMenu ?? "";
  const existingPlatformName = currentTheme?.platformName ?? "";
  const existingCentralPhone = currentTheme?.centralPhone ?? "";
  const existingCompanyPhone = currentTheme?.companyPhone ?? "";
  const existingPhrase = currentTheme?.phrase ?? "";
  const existingAndroidLink = currentTheme?.androidLink ?? "";
  const existingIosLink = currentTheme?.iosLink ?? "";

  const [form, setForm] = useState({
    licensedId: clientLicensedId,
    platformName: "",
    phrase: "",
    domain: FIXED_DOMAIN,
    currentLanguage: "pt-br",
    standardLanguage: "pt-br",
    companyPhone: "",
    centralPhone: "",
    iosLink: "",
    androidLink: "",
  });

  useEffect(() => {
    dispatch({ type: "title", value: t("PREVIEWS.CREATE_LICENSED.PAGE_TITLE")});
  }, [dispatch, t]);

  useEffect(() => {
    if (!clientLicensedId) return;
    setLoading(true);
    licenseService.fetchLicenses(clientLicensedId).then((data) => {
      const record = Array.isArray(data) ? data[0] : data;
      if (!record) return;
      setForm((prev) => ({
        ...prev,
        licensedId: record.licensedId ?? record.LicensedId ?? clientLicensedId,
        platformName: record.platformName ?? record.PlatformName ?? "",
        phrase: record.phrase ?? record.Phrase ?? "",
        currentLanguage: record.currentLanguage ?? record.CurrentLanguage ?? "pt-br",
        standardLanguage: record.standardLanguage ?? record.StandardLanguage ?? "pt-br",
        companyPhone: record.companyPhone ?? record.CompanyPhone ?? "",
        centralPhone: record.centralPhone ?? record.CentralPhone ?? "",
        iosLink: record.iosLink ?? record.IosLink ?? "",
        androidLink: record.androidLink ?? record.AndroidLink ?? "",
      }));
      if (record.colors) {
        try {
          const parsed = typeof record.colors === "string" ? JSON.parse(record.colors) : record.colors;
          setColors((prev) => ({ ...prev, ...parsed }));
        } catch {}
      }
      setImages({
        companyLogo: record.companyLogo ?? record.CompanyLogo ?? "",
        condensedMenu: record.condensedMenu ?? record.CondensedMenu ?? "",
        favicon: record.faveIcon ?? record.FaveIcon ?? record.favicon ?? record.Favicon ?? "",
        homeScreenImage: record.homeScreenImage ?? record.HomeScreenImage ?? "",
        internalLogo: record.internalLogo ?? record.InternalLogo ?? "",
      });
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientLicensedId]);

  useEffect(() => {
    setImages((prev) => ({
      companyLogo: prev.companyLogo || existingCompanyLogo,
      condensedMenu: prev.condensedMenu || existingCondensedMenu,
      favicon: prev.favicon || existingFavicon,
      homeScreenImage: prev.homeScreenImage || existingHomeScreenImage,
      internalLogo: prev.internalLogo || existingCondensedMenu,
    }));
  }, [existingCompanyLogo, existingCondensedMenu, existingFavicon, existingHomeScreenImage]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      platformName: prev.platformName || existingPlatformName,
      phrase: prev.phrase || existingPhrase,
      companyPhone: prev.companyPhone || existingCompanyPhone,
      centralPhone: prev.centralPhone || existingCentralPhone,
      iosLink: prev.iosLink || existingIosLink,
      androidLink: prev.androidLink || existingAndroidLink,
    }));
  }, [existingAndroidLink, existingCentralPhone, existingCompanyPhone, existingIosLink, existingPhrase, existingPlatformName]);

  const themeKey = themeMode;

  const handleFlatColor = (key, val) => {
    setColors((prev) => ({
      ...prev,
      [themeKey]: { ...prev[themeKey], [key]: val },
    }));
  };

  const handleNestedColor = (group, key, val) => {
    setColors((prev) => ({
      ...prev,
      [themeKey]: {
        ...prev[themeKey],
        [group]: { ...(prev[themeKey][group] || {}), [key]: val },
      },
    }));
  };

  const handleSave = async () => {
    
    if (!form.licensedId || !form.domain || !form.platformName) {
      console.error(t("PREVIEWS.CREATE_LICENSED.ERRORS.REQUIRED_FIELDS"));
      return;
    }

    setSaving(true);

    try {
      const payload = {
        licensedId: Number(form.licensedId),
        domain: form.domain,
        companyLogo: images.companyLogo || null,
        platformName: form.platformName,
        homeScreenImage: images.homeScreenImage || null,
        colors: JSON.stringify(colors),
        phrase: form.phrase || null,
        companyPhone: form.companyPhone || null,
        centralPhone: form.centralPhone || null,
        condensedMenu: images.condensedMenu || null,
        iosLink: form.iosLink || null,
        androidLink: form.androidLink || null,
        internalLogo: images.internalLogo || images.condensedMenu || null,
        faveIcon: images.favicon || null,
        standardLanguage: form.standardLanguage || "pt-br",
        currentLanguage: form.currentLanguage || "pt-br",
      };

      let result = null;

      // Se tem licensedId, tenta UPDATE primeiro
      if (form.licensedId) {
        result = await licenseService.updateLicensed(payload);
        // Se não encontrou, tenta CREATE
        if (result?.error && (result?.status === 404 || result?.status === 409)) {
          result = await licenseService.createLicensed(payload);
        }
      }

      if (result && !result.error) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } finally {
      setSaving(false);
    }
  };

  const themeColors = colors[themeKey];

  return (
    <div className="d-flex flex-column min-vh-100" style={{ background: "var(--bg-primary)", fontFamily: "Inter, sans-serif", "--preview-scrollbar-color": themeColors.bgScrollBar || "#FF7A1A" }}>
      {/* Top bar */}
      <div className="d-flex align-items-center justify-content-between px-3 py-2 shadow-sm" style={{background: "var(--bg-secondary)", borderColor: "var(--bg-scroll-bar)"}}>
        <div className="d-flex align-items-center gap-3">
          <div className="rounded d-flex align-items-center justify-content-center" style={{ background: "var(--bg-primary)", width: 40, height: 40 }}>
            <Palette size={16} color="var(--text-primary)" />
          </div>
          <div>
            <h1 className="h6 mb-0" style={{ color: 'var(--text-primary)' }}>{t("PREVIEWS.CREATE_LICENSED.PAGE_TITLE")}</h1>
            <p className="small text-muted mb-0">{t("PREVIEWS.CREATE_LICENSED.PAGE_SUBTITLE")}</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="btn btn-primary d-flex align-items-center gap-2"
          // style={{ background: saved ? "#28A745" : "#1F3A5F", borderColor: 'transparent' }}
        >
          <Save size={15} />
          {loading
            ? t("PREVIEWS.CREATE_LICENSED.LOADING") || "Carregando..."
            : saving
              ? t("PREVIEWS.CREATE_LICENSED.SAVING")
              : saved
                ? t("PREVIEWS.CREATE_LICENSED.SAVED")
                : t("PREVIEWS.CREATE_LICENSED.SAVE")}
        </button>
      </div>

      <div className="d-flex flex-grow-1 overflow-hidden" style={{ height: "calc(100vh - 73px)" }}>
        {/* Left sidebar - Tabs */}
        <div className=" border-end d-flex flex-column py-3" style={{ width: 160, minWidth: 160, background: "var(--bg-secondary)" }}>
          {TABS.map(({ id, labelKey, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className="btn d-flex align-items-center"
              style={{
                margin: '6px',
                justifyContent: 'flex-start',
                background: activeTab === id ? "#EDF2F9" : "transparent",
                color: activeTab === id ? "#1F3A5F" : "#8896A8",
                fontWeight: activeTab === id ? 600 : 400,
              }}
            >
              <Icon size={15} />
              <span style={{ marginLeft: 8, whiteSpace: "nowrap" }}>{t(labelKey)}</span>
            </button>
          ))}
        </div>

        {/* Center - Form */}
        <div
          className="overflow-auto p-3 preview-scroll-area"
          style={{ flex: "0 0 480px", minWidth: 480, maxWidth: 480 }}
        >
          {/* Theme toggle */}
          {activeTab !== "licensee" && activeTab !== "images" && (
            <div className="d-inline-flex align-items-center mb-3 p-1 bg-light rounded">
              <button
                onClick={() => setThemeMode("light_theme")}
                className="btn btn-sm"
                style={{
                  background: themeMode === "light_theme" ? "#fff" : "transparent",
                  color: themeMode === "light_theme" ? "#1F3A5F" : "#8896A8",
                  boxShadow: themeMode === "light_theme" ? "0 1px 4px rgba(0,0,0,0.10)" : "none",
                }}
              >
                <Sun size={13} /> <span style={{ marginLeft: 6 }}>{t("PREVIEWS.CREATE_LICENSED.THEME.LIGHT")}</span>
              </button>
              <button
                onClick={() => setThemeMode("dark_theme")}
                className="btn btn-sm ms-2"
                style={{
                  background: themeMode === "dark_theme" ? "#1F3A5F" : "transparent",
                  color: themeMode === "dark_theme" ? "#fff" : "#8896A8",
                }}
              >
                <Moon size={13} /> <span style={{ marginLeft: 6 }}>{t("PREVIEWS.CREATE_LICENSED.THEME.DARK")}</span>
              </button>
            </div>
          )}

          {/* Tab: Licensee */}
          {activeTab === "licensee" && (
            <div>
              <SectionTitle>{t("PREVIEWS.CREATE_LICENSED.LICENSEE.SECTION_TITLE")}</SectionTitle>
              {[
                { key: "licensedId", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.LICENSED_ID_LABEL"), type: "number", placeholder: t("PREVIEWS.CREATE_LICENSED.LICENSEE.LICENSED_ID_PLACEHOLDER") },
                { key: "platformName", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.PLATFORM_NAME_LABEL"), placeholder: t("PREVIEWS.CREATE_LICENSED.LICENSEE.PLATFORM_NAME_PLACEHOLDER") },
                { key: "phrase", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.PHRASE_LABEL"), placeholder: t("PREVIEWS.CREATE_LICENSED.LICENSEE.PHRASE_PLACEHOLDER") },
                { key: "domain", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.DOMAIN_LABEL"), placeholder: FIXED_DOMAIN },
                { key: "companyPhone", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.PHONE_LABEL"), placeholder: "(12) 3570-1102" },
                { key: "centralPhone", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.CENTRAL_PHONE_LABEL"), placeholder: "0800 000 0000" },
                { key: "iosLink", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.IOS_LINK_LABEL"), placeholder: "https://apps.apple.com/..." },
                { key: "androidLink", label: t("PREVIEWS.CREATE_LICENSED.LICENSEE.ANDROID_LINK_LABEL"), placeholder: "https://play.google.com/..." },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key} className="mb-3">
                  <label className="form-label small text-uppercase">{label}</label>
                  <input
                    type={type || "text"}
                    value={form[key]}
                    placeholder={placeholder}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                    readOnly={key === "licensedId" || key === "domain"}
                    disabled={key === "licensedId" || key === "domain"}
                    className="form-control"
                  />
                </div>
              ))}
              <div className="mb-3">
                <label className="form-label small text-uppercase">{t("PREVIEWS.CREATE_LICENSED.LICENSEE.LANGUAGE_LABEL")}</label>
                <select
                  value={form.currentLanguage}
                  onChange={(e) => setForm((p) => ({ ...p, currentLanguage: e.target.value }))}
                  className="form-select form-select-sm"
                >
                  <option value="pt-br">{t("PREVIEWS.CREATE_LICENSED.LICENSEE.LANG_PT_BR")}</option>
                  <option value="en-us">{t("PREVIEWS.CREATE_LICENSED.LICENSEE.LANG_EN_US")}</option>
                  <option value="es">{t("PREVIEWS.CREATE_LICENSED.LICENSEE.LANG_ES")}</option>
                </select>
              </div>
            </div>
          )}

          {/* Tab: Images */}
          {activeTab === "images" && (
            <div className="d-flex flex-column gap-3">
              <SectionTitle>{t("PREVIEWS.CREATE_LICENSED.IMAGES.SECTION_TITLE")}</SectionTitle>
              <ImageUploadField label={t("PREVIEWS.CREATE_LICENSED.IMAGES.COMPANY_LOGO_LABEL")} description={t("PREVIEWS.CREATE_LICENSED.IMAGES.COMPANY_LOGO_DESC")} value={images.companyLogo} onChange={(v) => setImages((p) => ({ ...p, companyLogo: v }))} id={"Logo"} />
              <ImageUploadField label={t("PREVIEWS.CREATE_LICENSED.IMAGES.HOME_SCREEN_LABEL")} description={t("PREVIEWS.CREATE_LICENSED.IMAGES.HOME_SCREEN_DESC")} value={images.homeScreenImage} onChange={(v) => setImages((p) => ({ ...p, homeScreenImage: v }))} id={"HomeScreen"} />
              <ImageUploadField label={t("PREVIEWS.CREATE_LICENSED.IMAGES.CONDENSED_MENU_LABEL")} description={t("PREVIEWS.CREATE_LICENSED.IMAGES.CONDENSED_MENU_DESC")} value={images.condensedMenu} onChange={(v) => setImages((p) => ({ ...p, condensedMenu: v }))} id={"InternalLogo"} />
              <ImageUploadField label={t("PREVIEWS.CREATE_LICENSED.IMAGES.FAVICON_LABEL")} description={t("PREVIEWS.CREATE_LICENSED.IMAGES.FAVICON_DESC")} value={images.favicon} onChange={(v) => setImages((p) => ({ ...p, favicon: v }))} id={"FaveIcon"} />
            </div>
          )}

          {/* Tab: Geral */}
          {activeTab === "geral" && (
            <div>
              <SectionTitle>{t("PREVIEWS.CREATE_LICENSED.COLORS.SECTION_TITLE")}</SectionTitle>
              <ColorGroupSection title={t("PREVIEWS.CREATE_LICENSED.COLORS.THEME")} fields={COLOR_SECTIONS.geral.flat.slice(0, 4)} values={themeColors} onChange={handleFlatColor} cols={COLOR_SECTIONS.geral.bootstrap?.cols || 2} />
              <ColorGroupSection title={t("PREVIEWS.CREATE_LICENSED.COLORS.BACKGROUNDS")} fields={COLOR_SECTIONS.geral.flat.slice(4)} values={themeColors} onChange={handleFlatColor} cols={COLOR_SECTIONS.geral.bootstrap?.cols || 2} />
              <ColorGroupSection title={t("PREVIEWS.CREATE_LICENSED.COLORS.TEXTS")} fields={COLOR_SECTIONS.geral.text} values={themeColors} onChange={handleFlatColor} cols={COLOR_SECTIONS.geral.bootstrap?.cols || 2} />
              <ColorGroupSection title={t("PREVIEWS.CREATE_LICENSED.COLORS.BORDERS")} fields={COLOR_SECTIONS.geral.border} values={themeColors} onChange={handleFlatColor} cols={COLOR_SECTIONS.geral.bootstrap?.cols || 2} />
              <ColorGroupSection title={t("PREVIEWS.CREATE_LICENSED.COLORS.STATUS")} fields={COLOR_SECTIONS.geral.status} values={themeColors} onChange={handleFlatColor} cols={COLOR_SECTIONS.geral.bootstrap?.cols || 2} />
            </div>
          )}

          {/* Nested color tabs */}
          {['menu', 'cards', 'filters', 'tables', 'buttons'].includes(activeTab) && (
            <div>
              <SectionTitle>
                {t(`PREVIEWS.CREATE_LICENSED.TABS.${
                  activeTab === "menu"
                    ? "MENU"
                    : activeTab === "cards"
                      ? "CARDS"
                      : activeTab === "filters"
                        ? "FILTERS"
                        : activeTab === "tables"
                          ? "TABLES"
                          : "BUTTONS"
                }`)}
              </SectionTitle>
              <div className="row g-2">
                {(() => {
                  const cols = COLOR_SECTIONS[activeTab].bootstrap?.cols || 2;
                  const colSize = Math.max(1, Math.min(12, Math.floor(12 / cols)));
                  const colClass = `col-12 col-md-${colSize}`;
                  return COLOR_SECTIONS[activeTab].fields.map(({ key, label }) => (
                    <div className={colClass} key={key}>
                      <ColorField
                        label={label}
                        value={themeColors[activeTab]?.[key]}
                        onChange={(val) => handleNestedColor(activeTab, key, val)}
                      />
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Right - Previews */}
        <div className="flex-grow-1 overflow-auto p-3 d-flex flex-column gap-3 preview-scroll-area" style={{ background: themeColors.bgSecondary || "#E8EDF5", minWidth: 0 }}>
          {/* Preview mode toggle */}
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <Eye size={15} className="text-muted" />
              <span className="small fw-semibold text-secondary">{t("PREVIEWS.CREATE_LICENSED.PREVIEW.REAL_TIME")}</span>
            </div>
            <div className="d-inline-flex align-items-center gap-1 p-1 bg-white rounded shadow-sm">
              <button
                onClick={() => setPreviewMode("general")}
                className="btn btn-sm"
                style={{ background: previewMode === "general" ? "#1F3A5F" : "transparent", color: previewMode === "general" ? "#fff" : "#8896A8" }}
              >
                <Monitor size={12} /> {t("PREVIEWS.CREATE_LICENSED.PREVIEW.GENERAL")}
              </button>
              <button
                onClick={() => setPreviewMode("login")}
                className="btn btn-sm ms-1"
                style={{ background: previewMode === "login" ? "#1F3A5F" : "transparent", color: previewMode === "login" ? "#fff" : "#8896A8" }}
              >
                <Smartphone size={12} /> {t("PREVIEWS.CREATE_LICENSED.PREVIEW.LOGIN")}
              </button>
              <button
                onClick={() => setPreviewMode("both")}
                className="btn btn-sm ms-1"
                style={{ background: previewMode === "both" ? "#1F3A5F" : "transparent", color: previewMode === "both" ? "#fff" : "#8896A8" }}
              >
                {t("PREVIEWS.CREATE_LICENSED.PREVIEW.BOTH")}
              </button>
            </div>
          </div>

          {/* Light preview */}
          <div className="d-flex flex-column gap-3">
            {(previewMode === "login" || previewMode === "both") && (
              <PreviewCard title={t("PREVIEWS.CREATE_LICENSED.PREVIEW.LOGIN_SCREEN")} subtitle="light">
                <LoginPreview colors={colors} images={images} platformName={form.platformName} phrase={form.phrase} theme="light_theme" />
              </PreviewCard>
            )}
            {(previewMode === "general" || previewMode === "both") && (
              <PreviewCard title={t("PREVIEWS.CREATE_LICENSED.PREVIEW.GENERAL_PREVIEW")} subtitle="light">
                <GeneralPreview colors={colors} theme="light_theme" />
              </PreviewCard>
            )}
            {(previewMode === "login" || previewMode === "both") && (
              <PreviewCard title={t("PREVIEWS.CREATE_LICENSED.PREVIEW.LOGIN_SCREEN")} subtitle="dark" dark>
                <LoginPreview colors={colors} images={images} platformName={form.platformName} phrase={form.phrase} theme="dark_theme" />
              </PreviewCard>
            )}
            {(previewMode === "general" || previewMode === "both") && (
              <PreviewCard title={t("PREVIEWS.CREATE_LICENSED.PREVIEW.GENERAL_PREVIEW")} subtitle="dark" dark>
                <GeneralPreview colors={colors} theme="dark_theme" />
              </PreviewCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="text-xs font-bold text-slate-800 mb-4 pb-2 border-b border-slate-100">{children}</h3>
  );
}

function PreviewCard({ title, subtitle, children, dark }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold" style={{color: "rgb(31, 58, 95)"}}>{title}</span>
        <span
          className="text-xs px-2 py-0.5 rounded-full font-medium"
        >
          {dark ? "🌙" : "☀️"}
        </span>
      </div>
      <div className="rounded-2xl overflow-hidden shadow-lg">
        {children}
      </div>
    </div>
  );
}